import { useState } from 'react';
import { api } from '../api';
import { todayISO } from '../lib/format';

export function ManualEntryModal({
  open,
  onClose,
  categories,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  categories: Array<{ slug: string; name: string; kind: string }>;
  onSaved: () => void;
}) {
  const [date, setDate] = useState(todayISO());
  const [amount, setAmount] = useState('');
  const [merchant, setMerchant] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('revenue_other');
  const [kind, setKind] = useState<'revenue' | 'expense'>('revenue');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (!open) return null;

  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      const cents = Math.round(parseFloat(amount) * 100);
      if (!Number.isFinite(cents) || cents === 0) throw new Error('Enter a non-zero amount');
      await api.manualEntry({
        occurred_at: `${date}T12:00:00Z`,
        amount_cents: kind === 'revenue' ? Math.abs(cents) : -Math.abs(cents),
        merchant: merchant || undefined,
        description: description || undefined,
        category_slug: category,
      });
      onSaved();
      onClose();
      setAmount(''); setMerchant(''); setDescription('');
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center bg-slate-900/40 p-4">
      <div className="mt-16 bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-semibold">Add manual entry</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl leading-none">&times;</button>
        </div>
        <div className="p-5 space-y-4">
          <div className="text-sm text-slate-500">
            For revenue or expenses that don't come through Plaid, Stripe, or Square —
            like a check deposit, wire, or ACH into the checking account.
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => { setKind('revenue'); setCategory('revenue_other'); }}
              className={`flex-1 py-2 rounded-md border text-sm font-medium ${kind === 'revenue' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white border-slate-300'}`}
            >Revenue</button>
            <button
              onClick={() => { setKind('expense'); setCategory('other_expense'); }}
              className={`flex-1 py-2 rounded-md border text-sm font-medium ${kind === 'expense' ? 'bg-rose-600 text-white border-rose-600' : 'bg-white border-slate-300'}`}
            >Expense</button>
          </div>

          <Field label="Date">
            <input type="date" value={date} onChange={e => setDate(e.target.value)}
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
          </Field>

          <Field label="Amount">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
              <input type="number" inputMode="decimal" step="0.01" value={amount} onChange={e => setAmount(e.target.value)}
                className="w-full border border-slate-300 rounded-md pl-7 pr-3 py-2 text-sm tnum" placeholder="0.00" />
            </div>
          </Field>

          <Field label="Source / merchant (optional)">
            <input value={merchant} onChange={e => setMerchant(e.target.value)}
              placeholder={kind === 'revenue' ? 'e.g. Client check — Acme Corp' : 'e.g. USPS'}
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
          </Field>

          <Field label="Notes (optional)">
            <input value={description} onChange={e => setDescription(e.target.value)}
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
          </Field>

          <Field label="Category">
            <select value={category} onChange={e => setCategory(e.target.value)}
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white">
              {categories.filter(c => kind === 'revenue' ? c.kind === 'revenue' : c.kind === 'expense').map(c => (
                <option key={c.slug} value={c.slug}>{c.name}</option>
              ))}
            </select>
          </Field>

          {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">{err}</div>}
        </div>
        <div className="p-5 border-t border-slate-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-md text-sm border border-slate-300">Cancel</button>
          <button onClick={submit} disabled={busy} className="px-4 py-2 rounded-md text-sm bg-slate-900 text-white disabled:opacity-50">
            {busy ? 'Saving…' : 'Save entry'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-slate-600 mb-1">{label}</div>
      {children}
    </label>
  );
}
